import { useCallback, useEffect, useRef, useState } from 'react';
import { trailBonusRoute, type PlayerProfile, type SlotDemoScene } from '@goodtime/engine';
import type { PlayOffer, SlotTrailState } from '@goodtime/shared';
import { funnelBeacon } from '../api/funnel';
import { withRetry } from '../api/road';
import { offerOfPlay } from '../api/roadFriends';
import { fetchTrail, openTrailGame, trailGameRefusal } from '../api/slot';
import { fetchWallet } from '../api/wallet';
import { playGame } from '../components/minigames/registry';

/** The line under the trail's board when its game couldn't open. */
export const TRAIL_GAME_RETRY = "Couldn't open the bonus just now. Tap the gift to try again.";

/** Resolves once the slot is between spins (a hold and spin the hop's spin
 *  started has played out), or `gone()` says to stop waiting. */
async function slotIdle(scene: SlotDemoScene, gone: () => boolean): Promise<void> {
  while (!scene.idle && !gone()) await new Promise((r) => setTimeout(r, 250));
}

export interface TrailGameOptions {
  sessionId: string | null;
  playerId: string | null;
  /** The slot whose trail is on screen: a game on another one comes to
   *  nothing here. */
  themeId: string | null;
  /** The slot scene, read when needed (it changes as slots open). */
  getScene: () => SlotDemoScene | null;
  /** Mirrors the gems the game pays as it opens (the gem toast). */
  profile: Pick<PlayerProfile, 'awardGems'>;
}

/** What opening the trail's game came to: the boxes to play instead (the
 *  server won't open it, and they're still there), or the bonus put away
 *  with the trail as it stands (null keeps the trail: the gift waits). */
export type TrailGameResult = { boxes: SlotTrailState } | { closed: SlotTrailState | null };

/**
 * The Sweet Trail's mini-games (trail_games): the bonus at the gift can be
 * one of the road's chance games instead of the boxes. `play` opens the game
 * the bonus rolled (its gems pop up as it opens), or reopens one left open,
 * and plays it on the slot's canvas to its end: coins fly into the counter,
 * free spins join the slot's, then the counter takes the server's wallet.
 * When the server can't be reached the gift waits, and `notice` says to tap
 * it again. Resolves with null when the slot changed meanwhile.
 */
export function useTrailGame({ sessionId, playerId, themeId, getScene, profile }: TrailGameOptions) {
  const [notice, setNotice] = useState<string | null>(null);
  const live = useRef({ sessionId, playerId, themeId, getScene, profile });
  live.current = { sessionId, playerId, themeId, getScene, profile };

  // A notice belongs to the slot it was said on.
  useEffect(() => setNotice(null), [themeId]);

  const play = useCallback(async (trail: SlotTrailState): Promise<TrailGameResult | null> => {
    const route = trailBonusRoute(trail);
    const sid = live.current.sessionId;
    const scene = live.current.getScene();
    if (route?.kind === 'boxes') return { boxes: trail };
    if (!route || !sid || !scene) return { closed: null };
    // Still the slot (and session) the game was opened on.
    const here = () => live.current.themeId === trail.themeId && live.current.sessionId === sid;
    setNotice(null);
    // A game never opens over the slot's own bonus, and the counter it ends
    // on the server's wallet is the slot's between spins.
    await slotIdle(scene, () => !here() || live.current.getScene() !== scene);
    if (!here()) return null;
    // The slot was built again meanwhile: the gift waits for a tap there.
    if (live.current.getScene() !== scene) return { closed: null };
    let offer: PlayOffer;
    let after: SlotTrailState;
    if (route.kind === 'resume') {
      // Left open (a reload, another tab): its screen replays what the
      // server has already answered, so nothing pays twice.
      try {
        offer = await withRetry(() => offerOfPlay(route.playId, sid));
      } catch {
        // Opening the slot again tries again; the server pays a game left
        // for an hour what it's worth.
        return here() ? { closed: { ...trail, game: null } } : null;
      }
      after = trail;
    } else {
      try {
        const res = await withRetry(() => openTrailGame(sid, trail.themeId, trail.level));
        offer = res.offer;
        after = res.trail;
        if (res.gems > 0) live.current.profile.awardGems(res.gems);
        funnelBeacon('trail_game', { playerId: live.current.playerId, detail: `${trail.themeId}:${trail.level}:${offer.kind}` });
      } catch (err) {
        const refused = trailGameRefusal(err);
        if (refused === 'TRAIL_NO_BONUS') {
          // Nothing waits any more (played in another tab): the trail as it is.
          const now = await fetchTrail(sid, trail.themeId);
          return here() ? { closed: now } : null;
        }
        if (!here()) return null;
        // Turned off, or a box already picked: the boxes are still there.
        if (refused) return { boxes: { ...trail, bonus: trail.bonus && { ...trail.bonus, game: null } } };
        setNotice(TRAIL_GAME_RETRY);
        return { closed: null };
      }
    }
    if (!here()) return null;
    const end = await playGame(scene, offer, sid);
    if (end.prize && end.prize.freeSpins > 0) scene.addFreeSpins(end.prize.freeSpins);
    // The coins have flown into the counter; it ends on the server's figure,
    // which already holds any spin's win still waiting to be collected.
    const pid = live.current.playerId;
    const wallet = pid ? await fetchWallet(pid).catch(() => null) : null;
    if (!here()) return null;
    if (wallet) scene.syncWalletCoins(wallet.coins);
    return { closed: { ...after, game: null } };
  }, []);

  return { play, notice };
}
